/**
 * Share Document Screen page component module.
 * @module advisor/shared/pages/ShareDocument
 */

import React, {useEffect, useState} from 'react';
import history from '../../../history';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import lazy from 'lazy.js';
import debug from 'debug';

import {
  Checkbox,
  Column,
  CriteriaSelector,
  DatePicker,
  DescriptiveText,
  Dropdown,
  LoadingSpinner,
  Page,
  RadioSet,
  Row,
  Steps,
  Table,
  Tabs,
  TextInput,
  Tile,
} from '../../../shared';
import {_openModal} from '../../../shared/reducers/modals';
import {_setNotification} from '../../../shared/reducers/headerNotification';
import {formatSelectedCriteria, gatherData, getUpdatedFilter, setSectionURI} from '../../../shared/utils/utils';
import axiosInstance from '../../../shared/utils/axios';

const log = debug('circleblack:share-document');

const ShareDocument = props => {
  const {userID, setNotification} = props;
  const today = moment().format('YYYY-MM-DD');
  const {id} = useParams();
  const [errorMessage, setErrorMessage] = useState(null);
  const [document, setDocument] = useState({});
  const [selectedHH, setSelectedHH] = useState([]);
  const [selectedDate, setSelectedDate] = useState(moment().add(1, 'd').format('YYYY-MM-DD'));
  const [selectedCriteria, setSelectedCriteria] = useState({0: {id: ''}});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [shareSchedule, setShareSchedule] = useState({immediate: 1});
  const [curStepIndx, setCurStepIndx] = useState(0);
  const [curTabIndx, setCurTabIndx] = useState(0);
  const getDocument = gatherData(`/documents/${id}`);
  const getAdvisedHouseholds = gatherData(`/advisors/${userID}/households`);
  const defaultParams = {};
  const timeOfDayOptions = [
    {label: 'Morning', value: 'Morning'},
    {label: 'Afternoon', value: 'Afternoon'},
    {label: 'Evening', value: 'Evening'},
  ];

  const resetShareSchedule = () => {
    setSelectedDate(moment().add(1, 'd').format('YYYY-MM-DD'));
    setShareSchedule({immediate: 1});
  };
  const submitShare = () => {
    const shareBody = {};
    const queryParams = {};

    shareBody.approved = document.approved;
    if (shareSchedule.immediate) {
      shareBody.immediate = shareSchedule.immediate;
    } else {
      shareBody.distributionDate = shareSchedule.distributionDate;
      shareBody.distributionTime = shareSchedule.distributionTime;
    }
    shareBody.highPriority = !!shareSchedule.highPriority;

    if (!curTabIndx) {
      shareBody.households = lazy(selectedHH).pluck('id').toArray();
    } else {
      shareBody.criteria = formatSelectedCriteria(selectedCriteria);
    }

    axiosInstance.post(`/documents/${document.id}/share`, shareBody, {params: queryParams})
      .then(() => {
        history.push(setSectionURI(`/${document.id}/sharing`));
        setNotification({
          message: `The ${document.type === 'Article'
            ? 'article'
            : 'document'} has been submitted. Once it is approved it will be sent to the selected households at the chosen time.`,
        });
      })
      .catch((err) => {
        let error = err.response && err.response;
        if (error.data && error.data.error && error.status === 404 && error.data.error.code === 132) {
          setErrorMessage(
            'None of your households matched the targeting criteria you created. Please choose a different set of criteria, or select the individual households.'
          );
        } else if (error.status < 500 && error.data && error.data.error && error.data.error.msg) {
          setErrorMessage(
            'Unfortunately we encountered an error processing the approval. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.'
          );
        } else {
          setErrorMessage(err.response.data.error.msg);
        }
      });
  };

  const householdsDataManager = {
    gatherData: inputValue => {
      if (document.cat === 'Report' && document.assocHouseholdID) {
        defaultParams.id = document.assocHouseholdID;
      }
      return getAdvisedHouseholds({
        ...getUpdatedFilter({...defaultParams, activatedAt: '!null'}),
        ...inputValue,
      })
        .catch(err => {
          log('err thrown');
          setErrorMessage(err);
          history.push(setSectionURI());
          throw err;
        });
    },
    setSelectedRows: rData => {
      setSelectedHH(rData);
    },
  };

  const householdsTab = {
    title: 'Households',
    value: 'HOUSEHOLDS',
    content: (!!document.id
      ? <Table dataManager={householdsDataManager}
               columns={[
                 {
                   title: 'Household',
                   dataPoint: 'name',
                   type: 'text',
                   minWidth: 120,
                   isSortable: true,
                 },
                 {
                   title: 'Email',
                   dataPoint: 'email',
                   type: 'text',
                   minWidth: 120,
                   isSortable: true,
                 },
               ]}
               defaultSort={{
                 column: 'name',
                 desc: false,
               }}
               searches={[
                 {
                   title: 'Household',
                   name: 'name',
                   isFuzzy: true,
                 },
                 {
                   title: 'Email',
                   name: 'email',
                   isFuzzy: true,
                 },
               ].filter(s => !!s)}
               select={{type: 'page', max: 100, preselectedRows: selectedHH}}
               rowIdentifier={'id'}
               height={'500px'}/>
      : <LoadingSpinner/>),
  };
  const targetCriteriaTab = {
    title: 'Target Criteria',
    value: 'TARGET CRITERIA',
    // TODO Modify Client Engagement CriteriaSelector to account for preselected values for back button works
    content: <CriteriaSelector selectedCriteria={selectedCriteria}
                               setSelectedCriteria={setSelectedCriteria}
                               setButtonDisabled={setButtonDisabled}/>,
  };

  // --------- Sections -------------

  const selectRecipients = <>
    {!!Object.keys(document).length && <DescriptiveText>Select the households you want to receive this {document.type === 'Article'
      ? 'article'
      : 'document'}, or create targeting criteria to easily send it to a
      group of households. {document.title} can only be shared with activated households.</DescriptiveText>}
    <Tabs
      tabs={[householdsTab, targetCriteriaTab]}
      displayTabTitles={true}
      tabWidth='1200px'
      minTabWidth='150px'
      defaultSelection={curTabIndx || 0}
      onChange={tabIndex => {
        setCurTabIndx(tabIndex);
      }}
    />
  </>;

  const selectShareSchedule = <>
    {!!Object.keys(document).length && <DescriptiveText>
      Select when you would like the {document.type === 'Article' ? 'Insight' : 'document'} to be sent to the selected
      households.</DescriptiveText>}
    <Column>
      <Row justifySubRow={'left'} width={'100%'}>
        <RadioSet dataPoint={{name: 'schedule', value: shareSchedule.immediate}}
                  options={[
                    {label: 'Immediately (as soon as it is approved)', value: 1},
                    {label: 'Select Date and Time', value: 0},
                  ]}
                  onChange={(name, val) => {
                    let newDate = shareSchedule.distributionDate || moment(selectedDate).toISOString();

                    setShareSchedule(val
                      ? {...shareSchedule, immediate: 1}
                      : {
                        ...shareSchedule,
                        immediate: 0,
                        distributionDate: newDate,
                        distributionTime: shareSchedule.distributionTime || 'Morning',
                      });
                  }}/>
      </Row>
      <Row justifySubRow={'left'}>
        <div>
          <DatePicker title={'Date'}
                      dataPoint={{name: 'date', value: selectedDate}}
                      minDate={today}
                      onChange={(name, value) => {
                        let newDate = moment(value).toISOString();
                        setShareSchedule({...shareSchedule, distributionDate: newDate});
                        setSelectedDate();
                        setSelectedDate(value);
                      }}
                      disabled={!!shareSchedule.immediate}/>
        </div>
        <div>
          <Dropdown dataPoint={{name: 'timeOfDay'}}
                    title={'Time of Day'}
                    placeholder={'Time of Day'}
                    width={'160px'}
                    defaultValue={timeOfDayOptions[0]}
                    options={timeOfDayOptions}
                    onChange={(name, value) => {
                      setShareSchedule(current => ({...current, distributionTime: value.value}));
                    }}
                    disabled={!!shareSchedule.immediate}/>
        </div>
      </Row>
      <Row justifySubRow={'left'}>
        <Checkbox title={'Mark as High Priority (this is only for compliance approval)'}
                  dataPoint={{name: 'highPriority', value: !!shareSchedule.highPriority || !!0}}
                  onChange={(name, value) => {
                    setShareSchedule({...shareSchedule, highPriority: value});
                  }}
                  width={'400px'}/>
      </Row>
    </Column>
  </>;

  // ----------- end of sections ---------

  useEffect(() => {
    if (!document.id) {
      getDocument({includeApproved: true, includeNotShareable: true})
        .then(doc => {
          if (doc.notShareable || doc.cat === 'Report' && !doc.assocHouseholdID) {
            history.push(setSectionURI());
            return;
          }
          setDocument(doc);
        })
        .catch(function error(err) {
          log('error was caught in useEffect', err);
          let res = err.response;
          if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
            setErrorMessage(
              'Unfortunately we encountered an error sharing the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.'
            );
          } else {
            setErrorMessage(res.data.error && res.data.error.msg);
          }
        });
    }
  }, []);

  return <Page>
    <Tile key={'ShareDocument'} title={'Share Document'}>
      <Row>
        <TextInput type={'text'}
                   title={'Document Name'}
                   placeholder={'Document Name'}
                   dataPoint={{name: 'title', value: document.title}}
                   onChange={() => {}}
                   readOnly={true}/>
      </Row>
      <Steps height={'800px'} stepIndex={curStepIndx} canStepBack={true} steps={[
        {
          title: 'Select Recipients',
          content: selectRecipients,
          stepIndex: 0,
          previous: {
            action: () => {
              history.push(setSectionURI(`/${id}/sharing`));
            },
          },
          next: {
            action: () => {if (!curStepIndx) setCurStepIndx(1);},
            disabled: (!selectedHH.length && curTabIndx === 0) || (curTabIndx === 1 && buttonDisabled),
          },
        },
        {
          title: 'Select Share Schedule',
          content: selectShareSchedule,
          previous: {
            action: () => {
              setCurStepIndx(0);
              resetShareSchedule();
            },
          },
          next: {
            label: 'Submit',
            action: () => {
              submitShare();
            },
          },
        },
      ]}
             cancelAction={() => history.push(setSectionURI(`/${id}/sharing`))}
             setErrorMessage={setErrorMessage}
             errorMessage={errorMessage}/>
    </Tile>
  </Page>;
};

const mapStateToProps = state => ({
  userID: state.session.profile.id,
});
const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  setNotification: note => dispatch(_setNotification(note)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShareDocument);

ShareDocument.propTypes = {
  userID: PropTypes.number.isRequired,
  /**
   * Notification for share confirmation in header. Takes object with message property.
   */
  setNotification: PropTypes.func.isRequired,
};
